import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Component, computed, effect, inject, PLATFORM_ID, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { catchError, forkJoin, map, of } from 'rxjs';
import type { HomePageData, SuccessStoriesPageData } from '../../../shared/models/marketing.models';
import { MarketingFooterComponent } from '../../components/marketing-footer.component';
import { MarketingHeaderComponent } from '../../components/marketing-header.component';
import { MarketingConsultationCtaComponent } from '../../components/marketing-consultation-cta.component';
import { SuccessStoriesFilterBarComponent } from '../../components/success-stories/success-stories-filter-bar.component';
import { SuccessStoriesGridComponent } from '../../components/success-stories/success-stories-grid.component';
import { SuccessStoriesHeroComponent } from '../../components/success-stories/success-stories-hero.component';
import { SuccessStoriesLoadMoreComponent } from '../../components/success-stories/success-stories-load-more.component';
import {
  heroStatsFromHome,
  mapSuccessStoryCards,
  matchesCategory,
  matchesSearch,
  STORIES_PAGE_SIZE,
  type StoryCategoryId,
} from '../../components/success-stories/success-stories.utils';
import { PagesService } from '../../../shared/services/pages.service';
import { buildPageTitle } from '../../../shared/constants/site-meta';

export type SuccessStoriesAllLoadState =
  | { kind: 'pending' }
  | { kind: 'error' }
  | {
      kind: 'ok';
      page: SuccessStoriesPageData;
      homeStats: Array<{ value: string; label: string }>;
      homeCta?: HomePageData['cta'];
      homeDestinations: string[];
    };

@Component({
  selector: 'app-marketing-success-stories-all-page',
  standalone: true,
  imports: [
    RouterLink,
    MarketingHeaderComponent,
    MarketingFooterComponent,
    SuccessStoriesHeroComponent,
    SuccessStoriesFilterBarComponent,
    SuccessStoriesGridComponent,
    SuccessStoriesLoadMoreComponent,
    MarketingConsultationCtaComponent,
  ],
  templateUrl: './success-stories-all-page.component.html',
})
export class MarketingSuccessStoriesAllPageComponent {
  private readonly pages = inject(PagesService);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly route = inject(ActivatedRoute);

  readonly activeCategory = signal<StoryCategoryId>('all');
  readonly searchQuery = signal('');
  readonly visibleCount = signal(STORIES_PAGE_SIZE);
  readonly loadingMore = signal(false);

  readonly highlightSlug = toSignal(this.route.fragment.pipe(map((f) => f ?? '')), {
    initialValue: '',
  });

  readonly pageState = toSignal(
    forkJoin({
      page: this.pages.getMarketingSuccessStories(),
      home: this.pages.getMarketingHome().pipe(catchError(() => of(null))),
    }).pipe(
      map(({ page, home }) => ({
        kind: 'ok' as const,
        page,
        homeStats: home?.stats ?? [],
        homeCta: home?.cta,
        homeDestinations: home?.destinations?.countries?.map((c) => c.name) ?? [],
      })),
      catchError(() =>
        isPlatformServer(this.platformId)
          ? of({ kind: 'pending' as const })
          : of({ kind: 'error' as const }),
      ),
    ),
    { initialValue: { kind: 'pending' as const } satisfies SuccessStoriesAllLoadState },
  );

  readonly allStories = computed(() => {
    const state = this.pageState();
    if (state.kind !== 'ok') return [];
    return mapSuccessStoryCards(state.page.items);
  });

  readonly filteredStories = computed(() => {
    const category = this.activeCategory();
    const query = this.searchQuery();
    return this.allStories().filter(
      (story) => matchesCategory(story, category) && matchesSearch(story, query),
    );
  });

  readonly visibleStories = computed(() => this.filteredStories().slice(0, this.visibleCount()));

  readonly hasMore = computed(() => this.visibleCount() < this.filteredStories().length);

  readonly hasAnyStories = computed(() => this.allStories().length > 0);

  readonly heroStats = computed(() => {
    const state = this.pageState();
    if (state.kind !== 'ok') return heroStatsFromHome();
    return heroStatsFromHome(state.homeStats);
  });

  constructor() {
    effect(() => {
      const state = this.pageState();
      if (state.kind === 'ok') {
        this.applySeo(state.page);
      }
    });

    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      const slug = this.highlightSlug();
      const state = this.pageState();
      if (!slug || state.kind !== 'ok') return;
      queueMicrotask(() =>
        document.getElementById(slug)?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      );
    });

    effect(() => {
      this.activeCategory();
      this.searchQuery();
      this.visibleCount.set(STORIES_PAGE_SIZE);
    });
  }

  setCategory(category: StoryCategoryId): void {
    this.activeCategory.set(category);
  }

  setSearch(query: string): void {
    this.searchQuery.set(query);
  }

  onLoadMore(): void {
    if (!this.hasMore() || this.loadingMore()) return;
    this.loadingMore.set(true);
    queueMicrotask(() => {
      this.visibleCount.update((count) => count + STORIES_PAGE_SIZE);
      this.loadingMore.set(false);
    });
  }

  private applySeo(page: SuccessStoriesPageData): void {
    const baseTitle = page.seo.title.replace(/\s*\|\s*Career Grad\s*$/i, '').trim();
    const documentTitle = buildPageTitle(`${baseTitle} — All Stories`);
    this.title.setTitle(documentTitle);
    if (page.seo.description) {
      this.meta.updateTag({ name: 'description', content: page.seo.description });
    }
  }
}
